import React, {useEffect, useState} from 'react'
import {useNavigate} from 'react-router-dom';
import SystemsGrid from '../Systems/SystemsGrid';
import BaseballIcon from '@assets/images/icons/sports/baseball.svg?react'
import FootballIcon from '@assets/images/icons/sports/football.svg?react'
import BasketballIcon from '@assets/images/icons/sports/basketball.svg?react'
import { AdjustmentsHorizontalIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import Pagination from '../Assets/Pagination';
import Dropdown from '../Assets/Dropdown';
import AuthProtected from '../HOCs/AuthProtected';
import { useGetPublicSystemsQuery , useGetUserSystemsQuery} from '../../services/api/systemsApi';
import { useGetUserSessionQuery } from '../../services/api/userApi';
import { LockClosedIcon } from '@heroicons/react/20/solid';

const MySystems = () => {
    const { data: userSessionData, isLoading: isUserSessionLoading, isError: isUserSessionError, isFetching: isUserSessionFetching } = useGetUserSessionQuery();
    const isSubActive = userSessionData?.isSubActive?.isActive;
    const [filtSystems, setFiltSystems] = useState([]);
    const systemsPerPage = 4;
    const navigate = useNavigate();
    const [view, setView] = useState({val: "private", label: "My Systems"});
    const [page, setPage] = useState(1);
    const [sport, setSport] = useState("mlb");
    const [pageCount, setPageCount] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [performanceFilter, setPerformanceFilter] = useState("all"); // "all", "positive", "best"
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

    // get public systems for the selected sport
    const { data: pubSystems, isLoading: isPubSystemsLoading, isError: isPubSystemsError } = useGetPublicSystemsQuery(
        { sport: sport },
        { skip: view.val !== "public" || !['mlb', 'nfl', 'nba'].includes(sport) || !isSubActive }
    );
    // get user systems (private and followed)
    const { data: userSystems, isLoading: isUserSystemsLoading, isError: isUserSystemsError } = useGetUserSystemsQuery();

    const viewOptions = [
        { value: "private", label: "My Systems" },
        { value: "followed", label: "Followed Systems", icon: isSubActive ? null : <LockClosedIcon width={15}/>, disabled: !isSubActive },
        { value: "public", label: "Public Systems", icon: isSubActive ? null : <LockClosedIcon width={15}/>, disabled: !isSubActive }
    ];

    const performanceOptions = [
        { value: "all", label: "All Systems" },
        { value: "positive", label: "Positive ROI" },
        { value: "best", label: "Best Performing" }
    ];

    // show component once rendered to avoid FOUC
    useEffect(()=> {
        const page = document.getElementById('comp-container');
        if (page) {
            page.classList.remove('opacity-0');
        }
    },[]);

    const calcPages = () => {
        setPageCount(Math.ceil(filtSystems.length / systemsPerPage));
    };

    // function to filter systems based on view / sport / search / performance
    const filterSystems = (selectedSport, viewObj, searchQuery, performance) => {
        const viewVal = viewObj.val;
        let systems = [];
        
        // First filter by view type
        switch(viewVal) {
            case("private"):
                systems = userSystems?.private?.filter(sys => sys.sport === selectedSport) || [];
                break;
            case("public"):
                let followedIds = {};
                
                // Compile all followed system IDs
                Object.keys(userSystems?.followed || {}).forEach(sportKey => {
                    userSystems?.followed[sportKey].forEach(sys => {
                        followedIds[sys._id] = true;
                    });
                });
                
                // Get systems from the public systems
                if (pubSystems && pubSystems.length > 0) {
                    systems = pubSystems.filter(system => !followedIds[system._id]);
                }
                break;
            case("followed"):
                systems = userSystems?.followed?.[selectedSport] || [];
                break; 
            default: 
                break;
        }
        
        // Then apply search filter if provided
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            systems = systems.filter(system => 
                system.name.toLowerCase().includes(query) || 
                (system.criteria && system.criteria.some(c => c.label.toLowerCase().includes(query)))
            );
        }
        
        // Then apply performance filter
        if (performance === "positive") {
            systems = systems.filter(system => system.roi > 0);
        } else if (performance === "best") {
            systems = [...systems].sort((a, b) => b.roi - a.roi);
        }
        
        setFiltSystems(systems);
    };

    // run whenever sport selection, view, public / followed systems, search or performance filter are updated
    useEffect(()=>{
        filterSystems(sport, view, searchTerm, performanceFilter);
    }, [sport, view, searchTerm, performanceFilter, userSystems, pubSystems]);

    // calc pages whenever filtered systems is set
    useEffect(()=>{
        calcPages();
        setPage(1);
    }, [filtSystems]);

    const handlePageChange = (value)=> {
        if(value < 1){
            setPage(1);
        }else if(value > pageCount){
            setPage(pageCount);
        }else{
            setPage(value);
        };
    };
    
    const handleViewChange = (value) =>{
        let label;
        if(value === "private"){
            label = "My Systems";
        }else if(value === "followed"){
            label = "Followed Systems";
        }else if(value === "public"){
            label = "Public Systems";
        };
        setView({val: value, label: label});
    };
    
    const handlePerformanceFilterChange = (value) => {
        setPerformanceFilter(value);
    };

    // Handle search input change
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    // Mobile filters dialog
    const toggleMobileFilters = () => {
        setMobileFiltersOpen(!mobileFiltersOpen);
    };

    return (
        <div id="comp-container" className="bg-white dark:bg-brand-dark-900 h-full transition-opacity duration-300 opacity-0">
            {/* Mobile filter dialog */}
            <div className={`fixed inset-0 z-40 lg:hidden ${mobileFiltersOpen ? 'block' : 'hidden'}`}>
                <div className="fixed inset-0 bg-black bg-opacity-25" onClick={toggleMobileFilters}></div>
                <div className="fixed inset-y-0 left-0 z-40 w-full max-w-xs overflow-y-auto bg-white dark:bg-gray-900 p-6 shadow-xl transform transition-transform duration-300">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-lg font-medium text-gray-900 dark:text-white">Filters</h2>
                        <button
                            type="button"
                            className="-mr-2 flex h-10 w-10 items-center justify-center rounded-md p-2 text-gray-400 hover:text-gray-500 dark:text-gray-300 dark:hover:text-white"
                            onClick={toggleMobileFilters}
                        >
                            <span className="sr-only">Close menu</span>
                            <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                    
                    {/* Mobile filters content */}
                    <div className="space-y-6">
                        <div>
                            <h3 className="text-sm font-medium text-gray-900 dark:text-white">View</h3>
                            <Dropdown
                                id="mobile-sysViewSelect"
                                title="Select View"
                                items={viewOptions}
                                activeItem={view.val}
                                onSelect={handleViewChange}
                                size="md"
                                variant="default"
                                className="mt-2 w-full"
                            />
                        </div>
                        
                        <div>
                            <h3 className="text-sm font-medium text-gray-900 dark:text-white">Sport</h3>
                            <div className="mt-2 flex flex-col gap-2">
                                <button 
                                    className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                        sport === "mlb" 
                                            ? "bg-brand-red-50 dark:bg-brand-red-900 text-brand-red-700 dark:text-white" 
                                            : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                                    }`}
                                    onClick={() => setSport("mlb")}
                                >
                                    <BaseballIcon 
                                        className="h-5 w-5" 
                                        fill={sport === "mlb" 
                                            ? "currentColor"
                                            : "currentColor"
                                        }
                                    />
                                    <span>MLB</span>
                                </button>
                                <button 
                                    className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                        sport === "nfl" 
                                            ? "bg-brand-red-50 dark:bg-brand-red-900 text-brand-red-700 dark:text-white" 
                                            : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                                    }`}
                                    onClick={() => setSport("nfl")}
                                >
                                    <FootballIcon 
                                        className="h-5 w-5" 
                                        fill={sport === "nfl" 
                                            ? "currentColor"
                                            : "currentColor"
                                        }
                                    />
                                    <span>NFL</span>
                                </button>
                                <button 
                                    className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                        sport === "nba" 
                                            ? "bg-brand-red-50 dark:bg-brand-red-900 text-brand-red-700 dark:text-white" 
                                            : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                                    }`}
                                    onClick={() => setSport("nba")}
                                >
                                    <BasketballIcon 
                                        className="h-5 w-5" 
                                        fill={sport === "nba" 
                                            ? "currentColor"
                                            : "currentColor"
                                        }
                                    />
                                    <span>NBA</span>
                                </button>
                            </div>
                        </div>
                        
                        <div>
                            <h3 className="text-sm font-medium text-gray-900 dark:text-white">Performance</h3>
                            <Dropdown
                                id="mobile-performance-filter"
                                title="Filter by Performance"
                                items={performanceOptions}
                                activeItem={performanceFilter}
                                onSelect={handlePerformanceFilterChange}
                                size="md"
                                variant="default"
                                className="mt-2 w-full"
                            />
                        </div>
                        
                        <div>
                            <h3 className="text-sm font-medium text-gray-900 dark:text-white">Search</h3>
                            <div className="relative mt-2">
                                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                                </div>
                                <input 
                                    type="text"
                                    className="block w-full rounded-md border-0 py-2.5 pl-10 text-gray-900 dark:text-white dark:bg-gray-800 dark:placeholder:text-gray-400 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-700 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand-red-500 sm:text-sm"
                                    placeholder="Search systems..."
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex flex-col lg:flex-row h-full">
                {/* Left sidebar (filters) */}
                <aside className="hidden lg:block lg:sticky lg:top-0 lg:h-screen lg:w-72 lg:border-r lg:border-gray-200 dark:lg:border-gray-700 lg:overflow-y-auto bg-white dark:bg-brand-dark-900 p-4 shrink-0">
                    <div className="sticky top-0 space-y-6">
                        {/* Header */}
                        <div className="pb-5 border-b border-gray-200 dark:border-gray-700">
                            <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                                {view.label}
                            </h1>
                            <div className="mt-3">
                        <Dropdown
                            id="sysViewSelect"
                            title="Select View"
                            items={viewOptions}
                            activeItem={view.val}
                            onSelect={handleViewChange}
                            size="md"
                            variant="default"
                                    className="w-full"
                                />
                                <button 
                                    onClick={()=>navigate('/system-builder', {state:{pageType:"new"}})}
                                    className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-md bg-brand-red-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-red-500"
                                >
                                    <span>Create System</span>
                                </button>
                    </div>
                </div>
                
                        {/* Search */}
                        <div>
                            <div className="relative">
                                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                                    <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
                                </div>
                                <div className="relative flex justify-start">
                                    <span className="bg-white dark:bg-brand-dark-900 pr-3 text-sm font-medium text-gray-900 dark:text-white">Search</span>
                                </div>
                            </div>
                            <div className="relative mt-4">
                                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                                </div>
                        <input 
                            type="text"
                                    className="block w-full rounded-md border-0 py-2.5 pl-10 text-gray-900 dark:text-white dark:bg-gray-800 dark:placeholder:text-gray-400 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-700 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand-red-500 sm:text-sm"
                            placeholder="Search systems..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                    </div>
                        </div>
                        
                        {/* Sport filter */}
                        <div>
                            <div className="relative">
                                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                                    <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
                                </div>
                                <div className="relative flex justify-start">
                                    <span className="bg-white dark:bg-brand-dark-900 pr-3 text-sm font-medium text-gray-900 dark:text-white">Sport</span>
                                </div>
                            </div>
                            <div className="space-y-2 mt-4">
                            <button 
                                    className={`flex w-full items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                        sport === "mlb" 
                                            ? "bg-brand-red-50 dark:bg-brand-red-900 text-brand-red-700 dark:text-white" 
                                            : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                                    }`}
                                onClick={() => setSport("mlb")}
                            >
                                    <BaseballIcon 
                                        className="h-5 w-5" 
                                        fill={sport === "mlb" 
                                            ? "currentColor"
                                            : "currentColor"
                                        }
                                    />
                                <span>MLB</span>
                            </button>
                            <button 
                                    className={`flex w-full items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                        sport === "nfl" 
                                            ? "bg-brand-red-50 dark:bg-brand-red-900 text-brand-red-700 dark:text-white" 
                                            : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                                    }`}
                                onClick={() => setSport("nfl")}
                            >
                                    <FootballIcon 
                                        className="h-5 w-5" 
                                        fill={sport === "nfl" 
                                            ? "currentColor"
                                            : "currentColor"
                                        }
                                    />
                                <span>NFL</span>
                            </button>
                            <button 
                                    className={`flex w-full items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                        sport === "nba" 
                                            ? "bg-brand-red-50 dark:bg-brand-red-900 text-brand-red-700 dark:text-white" 
                                            : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                                    }`}
                                onClick={() => setSport("nba")}
                            >
                                    <BasketballIcon 
                                        className="h-5 w-5" 
                                        fill={sport === "nba" 
                                            ? "currentColor"
                                            : "currentColor"
                                        }
                                    />
                                    <span>NBA</span>
                                </button>
                            </div>
                        </div>
                        
                        {/* Performance filter */}
                        <div>
                            <div className="relative">
                                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                                    <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
                                </div>
                                <div className="relative flex justify-start">
                                    <span className="bg-white dark:bg-brand-dark-900 pr-3 text-sm font-medium text-gray-900 dark:text-white">Performance</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 mt-4">
                                <AdjustmentsHorizontalIcon className="h-5 w-5 text-gray-500 dark:text-gray-300" />
                            <Dropdown
                                id="performance-filter"
                                title="Filter by Performance"
                                items={performanceOptions}
                                activeItem={performanceFilter}
                                onSelect={handlePerformanceFilterChange}
                                    size="md"
                                variant="default"
                                    className="w-full"
                            />
                        </div>
                    </div>
                </div>
                </aside>

                {/* Main content */}
                <main className="h-full flex-1 overflow-y-auto">
                    {/* Mobile header */}
                    <div className="lg:hidden sticky top-0 z-10 bg-white dark:bg-brand-dark-900 border-b border-gray-200 dark:border-gray-700 px-4 py-4">
                        <div className="flex flex-col space-y-4">
                            <div className="flex items-center justify-between">
                                <h1 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">
                                    {view.label}
                                </h1>
                                <button 
                                    onClick={()=>navigate('/system-builder', {state:{pageType:"new"}})}
                                    className="inline-flex items-center gap-1 rounded-md bg-brand-red-500 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-red-600"
                                >
                                    <span>Create System</span>
                                </button>
                            </div>
                            <button
                                type="button"
                                className="flex items-center justify-center gap-2 w-full p-2 rounded-md bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white border border-gray-200 dark:border-gray-700"
                                onClick={toggleMobileFilters}
                            >
                                <AdjustmentsHorizontalIcon className="h-5 w-5" />
                                <span className="text-sm font-medium">Filters</span>
                            </button>
                        </div>
                    </div>
                
                    {/* Systems content */}
                    <div className="overflow-y-scroll px-4 py-6 sm:px-6 lg:px-8">
                {filtSystems.length > 0 ? (
                    <>
                        <SystemsGrid 
                            systems={filtSystems.slice((page-1) * systemsPerPage, page*systemsPerPage)} 
                            sysType={view.val} 
                        />
                                
                                {/* Pagination */}
                                {pageCount > 1 && (
                                    <div className="mt-8">
                                        <Pagination 
                                            currentPage={page}
                                            totalPages={pageCount}
                                            onPageChange={handlePageChange}
                                        />
                                    </div>
                                )}
                    </>
                ) : (
                            <div className="flex flex-col items-center justify-center py-16 text-center">
                                <h3 className="mt-2 text-xl font-semibold text-gray-900 dark:text-white">No systems found</h3>
                                <p className="mt-1 text-gray-500 dark:text-gray-400">
                                    {view.val === 'private' ? 'Create your first system to get started' : 
                                    view.val === 'followed' ? 'Follow some public systems to see them here' : 
                                    'No systems match your current filters'}
                                </p>
                                <div className="mt-6">
                        {view.val === "private" && (
                                        <button 
                                onClick={() => navigate('/system-builder', {state:{pageType:"new"}})}
                                            className="rounded-md bg-brand-red-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-red-500"
                            >
                                Create a System
                                        </button>
                        )}
                        {view.val === "followed" && (
                            <button 
                                onClick={() => handleViewChange("public")}
                                            className="rounded-md bg-brand-red-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-red-500"
                            >
                                Browse Public Systems
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
};

export default AuthProtected(MySystems);